import { AttackPattern, PatternState } from '../AttackPattern.js';
import { ARENA } from '../../../../config/constants.js';

// How close (world px) the player needs to be to an unsafe line to count as
// standing on it — stands in for the old projectile radius now that the grid
// deals its damage directly instead of via traveling bullets.
const LINE_HIT_HALF_WIDTH = 3;

/**
 * Grid Barrage — a map-wide attack: every vertical grid line and every
 * horizontal one lights up across the whole arena, turning it into a
 * lattice. Exactly one column and one row are left clear, so the single safe
 * spot is where that safe column crosses the safe row — the player has to
 * read the telegraphed grid and get to that intersection before it resolves.
 * No projectiles are involved: once the telegraph completes, anywhere still
 * standing on an unsafe line takes a single hit of damage. Unlocks at phase 2.
 *
 * `config` (damage/cols/rows) is per-boss tuning, supplied by data/bosses.js.
 */
export class GridBarrage extends AttackPattern {
  constructor({ minPhase = 2, config }) {
    super({ name: 'Grid Barrage', telegraphDuration: 1.0, activeDuration: 1.6, minPhase, arenaControl: true });
    this.config = config;
  }

  onBegin() {
    const { cols, rows } = this.config;
    // Interior grid lines (skip the arena walls themselves).
    this._xs = [];
    for (let i = 1; i <= cols; i++) this._xs.push(ARENA.x + (ARENA.width * i) / (cols + 1));
    this._ys = [];
    for (let i = 1; i <= rows; i++) this._ys.push(ARENA.y + (ARENA.height * i) / (rows + 1));
    this._safeX = this._xs[Math.floor(Math.random() * this._xs.length)];
    this._safeY = this._ys[Math.floor(Math.random() * this._ys.length)];
  }

  /** Telegraph just ended — anyone still on an unsafe line gets hit once, no bullets involved. */
  onActiveStart(_boss, player) {
    const { damage } = this.config;
    const hitRadius = LINE_HIT_HALF_WIDTH + player.radius;
    const onUnsafeLine =
      this._xs.some((x) => x !== this._safeX && Math.abs(player.x - x) < hitRadius) ||
      this._ys.some((y) => y !== this._safeY && Math.abs(player.y - y) < hitRadius);
    if (onUnsafeLine) player.takeDamage(damage);
  }

  getVisual() {
    if (this.state !== PatternState.TELEGRAPH && this.state !== PatternState.ACTIVE) return null;
    return {
      kind: 'gridLines',
      xs: this._xs,
      ys: this._ys,
      safeX: this._safeX,
      safeY: this._safeY,
      imminent: this.state === PatternState.ACTIVE,
    };
  }
}
